import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CONTACT_CAPTURE_PAGE_SIZE } from '../helpers';
import { ContactCapture, ContactCaptureCampaign } from '../types';
import { ContactCaptureState } from './types';

const initialState: ContactCaptureState = {
  loading: { list: false, read: false, mutation: false },
  filter: {
    offset: 0,
    limit: CONTACT_CAPTURE_PAGE_SIZE,
    page: 0,
    query: '',
    type: undefined,
    status: undefined,
    sort: 'updatedAt_desc',
  },
  list: null,
  selected: null,
  recordRequest: null,
  recordError: null,
  listCompanyId: null,
  campaignOptions: null,
  activeRequestId: null,
  error: null,
};

const contactCaptureSlice = createSlice({
  name: 'contactCapture',
  initialState,
  reducers: {
    startRecordRequest: (state, action: PayloadAction<NonNullable<ContactCaptureState['recordRequest']>>) => {
      state.recordRequest = action.payload;
      state.loading.read = true;
      state.recordError = null;
      if (state.selected?.companyId !== action.payload.companyId || state.selected?.id !== action.payload.id) {
        state.selected = null;
      }
    },
    completeRecordRequest: (state, action: PayloadAction<{ requestId: string; record: ContactCapture }>) => {
      const { record, requestId } = action.payload;
      if (state.recordRequest?.requestId !== requestId
        || state.recordRequest.companyId !== record.companyId || state.recordRequest.id !== record.id) return;
      state.selected = record;
      state.loading.read = false;
      state.recordError = null;
    },
    failRecordRequest: (state, action: PayloadAction<{ requestId: string; message: string }>) => {
      if (state.recordRequest?.requestId !== action.payload.requestId) return;
      state.loading.read = false;
      state.recordError = action.payload.message;
    },
    clearRecord: state => {
      state.recordRequest = null;
      state.selected = null;
      state.loading.read = false;
      state.recordError = null;
    },
    startListRequest: (state, action: PayloadAction<{ companyId: string; requestId: string }>) => {
      state.loading.list = true;
      state.error = null;
      // Rows stay until the response lands, so sorting or paging does not blank the grid.
      if (state.listCompanyId !== action.payload.companyId) state.list = null;
      state.listCompanyId = action.payload.companyId;
      state.activeRequestId = action.payload.requestId;
    },
    completeListRequest: (state, action: PayloadAction<{ companyId: string; requestId: string; items: ContactCapture[]; count: number }>) => {
      if (state.activeRequestId !== action.payload.requestId || state.listCompanyId !== action.payload.companyId) return;
      state.list = { items: action.payload.items, count: action.payload.count };
      state.loading.list = false;
      state.activeRequestId = null;
    },
    failListRequest: (state, action: PayloadAction<{ companyId: string; requestId: string; message: string }>) => {
      if (state.activeRequestId !== action.payload.requestId || state.listCompanyId !== action.payload.companyId) return;
      state.error = action.payload.message;
      state.loading.list = false;
      state.activeRequestId = null;
    },
    setFilter: (state, action: PayloadAction<Partial<ContactCaptureState['filter']>>) => {
      state.filter = { ...state.filter, ...action.payload };
    },
    setLoading: (state, action: PayloadAction<Partial<ContactCaptureState['loading']>>) => {
      state.loading = { ...state.loading, ...action.payload };
    },
    setSelected: (state, action: PayloadAction<ContactCapture | null>) => {
      // A mutation for a previous page must not replace the record now being edited.
      if (action.payload && state.recordRequest && (
        state.recordRequest.companyId !== action.payload.companyId || state.recordRequest.id !== action.payload.id
      )) return;
      state.selected = action.payload;
    },
    setCampaignOptions: (
      state,
      action: PayloadAction<{ companyId: string; items: ContactCaptureCampaign[] }>,
    ) => {
      state.campaignOptions = { ...action.payload, lookups: {}, fetchedAt: Date.now() };
    },
    /** A campaign created or resolved outside the options request, for the same company. */
    rememberCampaign: (
      state,
      action: PayloadAction<{ companyId: string; campaign: ContactCaptureCampaign; selectable: boolean }>,
    ) => {
      const { companyId, campaign, selectable } = action.payload;
      if (state.campaignOptions?.companyId !== companyId) return;
      if (selectable) {
        state.campaignOptions.items = [
          campaign, ...state.campaignOptions.items.filter(item => item.id !== campaign.id),
        ];
      } else {
        state.campaignOptions.lookups[campaign.id] = campaign;
      }
    },
    updateListRecord: (state, action: PayloadAction<ContactCapture>) => {
      if (!state.list || state.listCompanyId !== action.payload.companyId) return;
      const index = state.list.items.findIndex(item => item.id === action.payload.id);
      if (index >= 0) state.list.items[index] = action.payload;
    },
    resetList: state => {
      state.list = null;
      state.listCompanyId = null;
      state.activeRequestId = null;
      state.loading.list = false;
      state.error = null;
    },
  },
});

export const {
  startRecordRequest,
  completeRecordRequest,
  failRecordRequest,
  clearRecord,
  startListRequest,
  completeListRequest,
  failListRequest,
  setFilter,
  setLoading,
  setCampaignOptions,
  rememberCampaign,
  setSelected,
  updateListRecord,
  resetList,
} = contactCaptureSlice.actions;

export const contactCaptureReducer = contactCaptureSlice.reducer;
